import type FetchService from '../FetchService';
import type { ApiRequestBodyType, ApiRequestPathParamType, ApiResponseType } from '../types';
import type {
  ApiMarkDeceasedValues,
  ApiUser,
  ApiUserFrontendSyncConfiguration,
  ApiUserMailConfiguration,
} from './types';

class UserApi {
  constructor(private fetchService: FetchService) {}

  getCurrentUser() {
    return this.fetchService.get<ApiUser>('/user');
  }

  getFrontendSyncConfiguration() {
    return this.fetchService.get<ApiUserFrontendSyncConfiguration>(
      '/user/frontend-sync/configuration',
    );
  }

  registerUser(values: ApiRequestBodyType<'setRegistration'>) {
    return this.fetchService.post<ApiUser>('/user/register', values);
  }

  updateUser(values: ApiRequestBodyType<'updateUserInfo'>) {
    return this.fetchService.put<ApiUser>('/user', values);
  }

  markDeceased(values: ApiMarkDeceasedValues) {
    return this.fetchService.post<void>('/public/mark-deceased', {
      nrn: values.nrn,
      deceasedNotificationDate: values.deceasedNotificationDate,
      deceasedDate: values.deceasedDate,
    });
  }

  unmarkDeceased(nrn: string) {
    return this.fetchService.post<void>('/public/unmark-deceased', {
      nrn,
    });
  }

  startDeactivation() {
    return this.fetchService.post<ApiResponseType<'initiateDeactivation'>>(
      '/user/initiate-deactivation',
    );
  }

  completeDeactivation({ deactivationCode }: ApiRequestPathParamType<'finalizeDeactivation'>) {
    return this.fetchService.post<ApiResponseType<'finalizeDeactivation'>>(
      `/user/finalize-deactivation/${deactivationCode}`,
    );
  }

  cancelDeactivation() {
    return this.fetchService.post<ApiResponseType<'cancelDeactivation'>>(
      '/user/cancel-deactivation',
    );
  }

  getUserPreferences() {
    return this.fetchService.get<ApiResponseType<'getUserPreferences'>>('/user/preferences');
  }

  setUserPreferences(preferences: ApiRequestBodyType<'setUserPreferences'>) {
    return this.fetchService.put<ApiResponseType<'getUserPreferences'>>(
      '/user/preferences',
      preferences,
    );
  }

  getUserMailConfiguration() {
    return this.fetchService.get<ApiUserMailConfiguration>('/user/mail-configuration');
  }

  setUserMailConfiguration(mailConfiguration: ApiUserMailConfiguration) {
    return this.fetchService.put<ApiUserMailConfiguration>(
      '/user/mail-configuration',
      mailConfiguration,
    );
  }
}

export default UserApi;
